import classNames from 'classnames';
import moment from 'moment';
import React, { useMemo, useRef, useState } from 'react';
import { DayPickerProps } from 'react-day-picker';
import { useTranslation } from '~i18n';
import { IconDatepicker, IconRight } from '~svg-components';
import DayPicker, { DayPickerFieldHandle } from './day-picker';
import styles from './index.module.scss';

interface DateRangePickerProps {
  startDate?: Date;
  endDate?: Date;
  fromMonth: Date;
  toMonth: Date;
  disabled?: boolean;
  disabledStartDays?: DayPickerProps['disabledDays'];
  disabledEndDays?: DayPickerProps['disabledDays'];
  selectableStartDates?: string[];
  selectableEndDates?: string[];
  endDateLoading?: boolean;
  classes?: {
    root?: string;
    container?: string;
    startDateContainer?: string;
    endDateContainer?: string;
    icon?: string;
    iconRight?: string;
  };
  onStartDateChange?: (value: Date) => void;
  onEndDateChange?: (value: Date) => void;
}

function buildSelectableDaysModifier(
  selectableDates: string[] | undefined,
  fromMonth: Date,
  toMonth: Date
): DayPickerProps['disabledDays'] | undefined {
  if (selectableDates === undefined) return undefined;
  if (selectableDates.length === 0) return () => true;
  const allowedSet = new Set(selectableDates.map(d => moment(d).format('YYYY-MM-DD')));
  return (day: Date) => {
    const dayMoment = moment(day);
    if (dayMoment.isBefore(fromMonth, 'month') || dayMoment.isAfter(toMonth, 'month')) return true;
    return !allowedSet.has(dayMoment.format('YYYY-MM-DD'));
  };
}

function getFirstSelectableMonth(selectableDates: string[] | undefined): Date | undefined {
  if (!selectableDates || selectableDates.length === 0) return undefined;
  const sorted = [...selectableDates].sort();
  const first = moment(sorted[0]);
  return first.isValid() ? first.startOf('month').toDate() : undefined;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate: startDateProp,
  endDate: endDateProp,
  fromMonth,
  toMonth,
  disabled,
  disabledStartDays,
  disabledEndDays,
  selectableStartDates,
  selectableEndDates,
  endDateLoading,
  classes,
  onStartDateChange,
  onEndDateChange,
}) => {
  const [t] = useTranslation('student_portal');
  const endPickerRef = useRef<DayPickerFieldHandle>(null);

  const startDate = startDateProp;
  const endDate = endDateProp;

  const [startPickerOpen, setStartPickerOpen] = useState(false);
  const [endPickerOpen, setEndPickerOpen] = useState(false);

  const anyPickerOpen = startPickerOpen || endPickerOpen;

  const startDisabledDays = useMemo(
    () =>
      disabledStartDays ?? buildSelectableDaysModifier(selectableStartDates, fromMonth, toMonth),
    [disabledStartDays, selectableStartDates, fromMonth, toMonth]
  );

  const endDisabledDays = useMemo(
    () => disabledEndDays ?? buildSelectableDaysModifier(selectableEndDates, fromMonth, toMonth),
    [disabledEndDays, selectableEndDates, fromMonth, toMonth]
  );

  const startInitialMonth = useMemo(() => getFirstSelectableMonth(selectableStartDates), [
    selectableStartDates,
  ]);

  const endInitialMonth = useMemo(() => getFirstSelectableMonth(selectableEndDates), [
    selectableEndDates,
  ]);

  return (
    <div className={classNames(styles.root, classes?.root)}>
      <div
        className={classNames(
          styles.container,
          {
            [styles.containerActive]: anyPickerOpen && !disabled,
            [styles.containerDisabled]: !!disabled,
          },
          classes?.container
        )}
      >
        <div className={classNames(styles.startDateContainer, classes?.startDateContainer)}>
          <DayPicker
            value={startDate}
            fromMonth={fromMonth}
            toMonth={toMonth}
            initialMonth={startInitialMonth}
            disabled={disabled}
            disabledDays={startDisabledDays}
            placeholder={t('start_date')}
            onVisibleChange={setStartPickerOpen}
            onSelectDate={date => {
              onStartDateChange?.(date);
              setTimeout(() => endPickerRef.current?.open(), 100);
            }}
          />
        </div>

        <IconRight
          width="13"
          height="12"
          fill="var(--color-purple_4)"
          className={classNames(styles.iconRight, classes?.iconRight)}
        />

        <div className={classNames(styles.endDateContainer, classes?.endDateContainer)}>
          <DayPicker
            ref={endPickerRef}
            value={endDate}
            fromMonth={fromMonth}
            toMonth={toMonth}
            initialMonth={endInitialMonth}
            disabled={disabled || !startDate}
            loading={endDateLoading}
            disabledDays={endDisabledDays}
            placeholder={t('end_date')}
            onVisibleChange={setEndPickerOpen}
            onSelectDate={date => {
              onEndDateChange?.(date);
            }}
          />
        </div>

        <IconDatepicker
          width="14.7"
          height="15.7"
          fill="var(--color-purple_4)"
          className={classNames(styles.icon, classes?.icon)}
        />
      </div>
    </div>
  );
};

export default DateRangePicker;
